import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Chip, CircularProgress, IconButton, Alert, Divider,
  Accordion, AccordionSummary, AccordionDetails, LinearProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { EDUGATE_API_URL } from '../config/serviceConfig';

// 雷达图标签保持完整，通过换行适配有限画布，避免用省略号隐藏维度含义。
const splitRadarLabel = (value, maxChars = 6) => {
  const chars = Array.from(String(value || ''));
  const lines = [];
  for (let index = 0; index < chars.length; index += maxChars) {
    lines.push(chars.slice(index, index + maxChars).join(''));
  }
  return lines.length ? lines : ['-'];
};

// ========== SVG 工具组件 ==========

// 迷你柱状图（得分走势，对齐 Vue 的 mini-bar）
const MiniBarChart = ({ data, maxVal = 5, height = 24 }) => {
  if (!data || data.length === 0) return null;
  const barW = 8, gap = 2;
  const w = data.length * (barW + gap);
  return (
    <svg width={w} height={height} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      {data.map((s, i) => {
        const h = Math.max(3, (s / maxVal) * height);
        const color = s >= 4 ? '#4CAF50' : (s >= 3 ? '#FF9800' : '#f44336');
        return <rect key={i} x={i * (barW + gap)} y={height - h} width={barW} height={h} rx={2} fill={color} />;
      })}
    </svg>
  );
};

// 迷你折线图
const MiniLineChart = ({ data, width = 120, height = 36, color = '#4CAF50' }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 0.1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pad = 4;
  const w = width - pad * 2, h = height - pad * 2;
  const points = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * w;
    const y = pad + h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  const lastX = pad + w;
  const lastY = pad + h - ((data[data.length - 1] - min) / range) * h;
  return (
    <svg width={width} height={height}>
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={3} fill={color} />
    </svg>
  );
};

// 雷达图（能力画像）
const RadarChart = ({ dimStages, size = 280 }) => {
  if (!dimStages || dimStages.length === 0) return null;
  const cx = size / 2, cy = size / 2, maxR = size / 2 - 68;
  const n = dimStages.length;
  const angleStep = (2 * Math.PI) / n;
  const maxScore = 5;
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];

  const gridPaths = gridLevels.map(lv => {
    const pts = Array.from({ length: n }, (_, i) => {
      const a = i * angleStep - Math.PI / 2;
      return `${cx + maxR * lv * Math.cos(a)},${cy + maxR * lv * Math.sin(a)}`;
    });
    return pts.join(' ');
  });

  const dataPoints = dimStages.map((d, i) => {
    const a = i * angleStep - Math.PI / 2;
    const r = ((d.maxScore || 0) / maxScore) * maxR;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(' ');

  return (
    <svg width={size} height={size} style={{ display: 'block', margin: '0 auto' }}>
      {gridPaths.map((pts, i) => (
        <g key={i}>
          <polygon points={pts} fill={i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.01)'} stroke="rgba(255,255,255,0.08)" strokeWidth={1} />
          <text x={cx + 4} y={cy - maxR * gridLevels[i] + 3}
            fill="rgba(255,255,255,0.2)" fontSize={9}>{(maxScore * gridLevels[i]).toFixed(0)}</text>
        </g>
      ))}
      {dimStages.map((_, i) => {
        const a = i * angleStep - Math.PI / 2;
        return <line key={i} x1={cx} y1={cy} x2={cx + maxR * Math.cos(a)} y2={cy + maxR * Math.sin(a)} stroke="rgba(255,255,255,0.06)" />;
      })}
      <polygon points={dataPoints} fill="rgba(33,150,243,0.25)" stroke="#2196F3" strokeWidth={2} />
      {dimStages.map((d, i) => {
        const a = i * angleStep - Math.PI / 2;
        const r = ((d.maxScore || 0) / maxScore) * maxR;
        return <circle key={i} cx={cx + r * Math.cos(a)} cy={cy + r * Math.sin(a)} r={4} fill="#2196F3" stroke="#1a1a1a" strokeWidth={1} />;
      })}
      {dimStages.map((d, i) => {
        const a = i * angleStep - Math.PI / 2;
        const lr = maxR + 36;
        const name = d.dimensionName || d.DimensionName || '';
        const lines = splitRadarLabel(name);
        const lineHeight = 12;
        const x = cx + lr * Math.cos(a);
        const y = cy + lr * Math.sin(a);
        const startY = y - (lines.length * lineHeight) / 2 - 3;
        return (
          <g key={`l${i}`}>
            <text x={x} y={startY} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize={10}>
              {lines.map((line, lineIndex) => (
                <tspan key={`${i}-${lineIndex}`} x={x} dy={lineIndex === 0 ? 0 : lineHeight}>
                  {line}
                </tspan>
              ))}
            </text>
            <text x={x} y={startY + lines.length * lineHeight + 4} textAnchor="middle" fill="#2196F3" fontSize={10} fontWeight="600">{(d.maxScore || 0).toFixed(1)}</text>
          </g>
        );
      })}
    </svg>
  );
};

// 维度趋势双轴图（左轴:累计证据数-堆叠柱状图 / 右轴:历史最高分-折线图）
const TrendChart = ({ dimensionMaxScores, dimensionAccumTrends, evalLabels, t }) => {
  const [hoverIdx, setHoverIdx] = React.useState(null);

  const maxScoreTrends = dimensionMaxScores || {};
  const accumTrends = dimensionAccumTrends || {};
  const allDims = [...new Set([...Object.keys(accumTrends), ...Object.keys(maxScoreTrends)])];
  if (allDims.length === 0) return null;
  const evalCount = Math.max(...allDims.map(d => (accumTrends[d]?.length || 0)), ...allDims.map(d => (maxScoreTrends[d]?.length || 0)));
  if (evalCount < 2) return null;

  const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#48b8d0', '#ff9f7f'];

  const chartHeight = 350;
  const pL = 68, pR = 68, pT = 24, pB = 80;
  const minWidth = 520;
  const barGroupWidth = Math.max(60, 120 - evalCount * 5);
  const calcWidth = pL + pR + evalCount * barGroupWidth + (evalCount - 1) * 20;
  const width = Math.max(minWidth, calcWidth);
  const cW = width - pL - pR;
  const cH = chartHeight - pT - pB;

  const stackedTotals = Array.from({ length: evalCount }, (_, i) =>
    allDims.reduce((sum, dim) => sum + ((accumTrends[dim] || [])[i] || 0), 0)
  );
  const maxEvidence = Math.max(...stackedTotals, 1);
  const leftTicks = (() => {
    const nice = [1, 2, 5, 10, 15, 20, 25, 30, 40, 50, 60, 80, 100];
    const step = nice.find(n => n * 5 >= maxEvidence) || Math.ceil(maxEvidence / 5);
    const ticks = [];
    for (let v = 0; v <= maxEvidence + step; v += step) { ticks.push(v); if (ticks.length >= 6) break; }
    return ticks;
  })();
  const leftMax = leftTicks[leftTicks.length - 1] || maxEvidence;
  const rightMax = 5;
  const rightTicks = [0, 1, 2, 3, 4, 5];
  const xStep = cW / evalCount;
  const xCenter = (i) => pL + xStep * (i + 0.5);
  const barW = Math.min(Math.max(18, xStep * 0.5), 40);

  return (
    <Box sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.15)', borderRadius: 3 } }}>
      <svg width={width} height={chartHeight} style={{ display: 'block' }}
        onMouseLeave={() => setHoverIdx(null)}>
        {leftTicks.map((v, i) => {
          const y = pT + cH - (v / leftMax) * cH;
          return (
            <g key={`gl${i}`}>
              <line x1={pL} y1={y} x2={width - pR} y2={y} stroke="rgba(255,255,255,0.06)" strokeDasharray={v === 0 ? 'none' : '3,3'} />
            </g>
          );
        })}
        <line x1={pL} y1={pT} x2={pL} y2={pT + cH} stroke="#5470c6" strokeWidth={1} opacity={0.5} />
        <text x={pL - 8} y={pT - 8} textAnchor="end" fill="#5470c6" fontSize={10} fontWeight={500}>{t('growth_accumulated_evidence')}</text>
        {leftTicks.map((v, i) => {
          const y = pT + cH - (v / leftMax) * cH;
          return <text key={`lt${i}`} x={pL - 8} y={y + 4} textAnchor="end" fill="rgba(255,255,255,0.35)" fontSize={10}>{v}{t('growth_evidence_unit')}</text>;
        })}
        <line x1={width - pR} y1={pT} x2={width - pR} y2={pT + cH} stroke="#ee6666" strokeWidth={1} opacity={0.5} />
        <text x={width - pR + 8} y={pT - 8} textAnchor="start" fill="#ee6666" fontSize={10} fontWeight={500}>{t('growth_max_score')}</text>
        {rightTicks.map((v, i) => {
          const y = pT + cH - (v / rightMax) * cH;
          return <text key={`rt${i}`} x={width - pR + 8} y={y + 4} textAnchor="start" fill="rgba(255,255,255,0.35)" fontSize={10}>{v}{t('growth_score_unit')}</text>;
        })}
        {Array.from({ length: evalCount }, (_, i) => (
          <text key={`xl${i}`} x={xCenter(i)} y={pT + cH + 18} textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize={10}>
            {evalLabels?.[i] || t('growth_training_number', { number: i + 1 })}
          </text>
        ))}
        {Array.from({ length: evalCount }, (_, evalIdx) => {
          let accY = 0;
          return allDims.map((dimName, di) => {
            const v = (accumTrends[dimName] || [])[evalIdx] || 0;
            if (v <= 0) return null;
            const barH = (v / leftMax) * cH;
            const x = xCenter(evalIdx) - barW / 2;
            const y = pT + cH - accY - barH;
            accY += barH;
            return <rect key={`bar-${evalIdx}-${di}`} x={x} y={y} width={barW} height={barH} fill={colors[di % colors.length]} opacity={0.7} rx={2} stroke="rgba(0,0,0,0.2)" strokeWidth={0.5} />;
          });
        })}
        {allDims.map((dimName, di) => {
          const vals = maxScoreTrends[dimName] || [];
          if (vals.length < 2) return null;
          const color = colors[di % colors.length];
          const points = vals.map((v, i) => ({ x: xCenter(i), y: pT + cH - (v / rightMax) * cH, v }));
          const pathD = points.map((p, i) => {
            if (i === 0) return `M ${p.x},${p.y}`;
            const prev = points[i - 1];
            const cpx = (prev.x + p.x) / 2;
            return `C ${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;
          }).join(' ');
          return (
            <g key={`line-${di}`}>
              <path d={pathD} fill="none" stroke={color} strokeWidth={2.5} opacity={0.9} />
              {points.map((p, i) => <circle key={`dot-${di}-${i}`} cx={p.x} cy={p.y} r={4} fill={color} stroke="#1a1a1a" strokeWidth={1.5} />)}
            </g>
          );
        })}
        {Array.from({ length: evalCount }, (_, i) => (
          <rect key={`hover-${i}`} x={xCenter(i) - xStep / 2} y={pT} width={xStep} height={cH} fill="transparent" onMouseEnter={() => setHoverIdx(i)} />
        ))}
        {hoverIdx !== null && <line x1={xCenter(hoverIdx)} y1={pT} x2={xCenter(hoverIdx)} y2={pT + cH} stroke="rgba(255,255,255,0.2)" strokeWidth={1} strokeDasharray="4,3" />}
        {hoverIdx !== null && (() => {
          const ttX = xCenter(hoverIdx);
          const ttFlip = ttX > width / 2;
          const boxW = 200, boxH = 24 + allDims.length * 18;
          const bx = ttFlip ? ttX - boxW - 12 : ttX + 12;
          const by = pT + 8;
          return (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={bx} y={by} width={boxW} height={boxH} rx={6} fill="rgba(30,30,30,0.95)" stroke="rgba(255,255,255,0.15)" strokeWidth={1} />
              <text x={bx + 10} y={by + 16} fill="#fff" fontSize={11} fontWeight={600}>{evalLabels?.[hoverIdx] || t('growth_training_number', { number: hoverIdx + 1 })}</text>
              {allDims.map((dim, di) => {
                const ev = (accumTrends[dim] || [])[hoverIdx] || 0;
                const sc = (maxScoreTrends[dim] || [])[hoverIdx];
                const shortName = dim.length > 8 ? dim.slice(0, 8) + '…' : dim;
                return (
                  <g key={di}>
                    <rect x={bx + 10} y={by + 24 + di * 18} width={8} height={8} rx={2} fill={colors[di % colors.length]} />
                    <text x={bx + 24} y={by + 32 + di * 18} fill="rgba(255,255,255,0.7)" fontSize={10}>
                      {t('growth_dimension_name', { 
                        name: shortName, 
                        count: ev,
                        score: sc !== undefined ? t('growth_max_score_detail', { score: sc.toFixed(1) }) : ''
                      })}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })()}
      </svg>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 1, mt: 1, justifyContent: 'center' }}>
        {allDims.map((d, i) => (
          <Box key={d} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, bgcolor: colors[i % colors.length], borderRadius: '2px', opacity: 0.7 }} />
            <Box sx={{ width: 18, height: 2, bgcolor: colors[i % colors.length], borderRadius: 1 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}>{d.length > 8 ? d.slice(0, 8) + '…' : d}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// 阶段颜色
const stageColor = (stage) => {
  if (!stage) return '#888';
  if (stage.includes('精熟')) return '#4CAF50';
  if (stage.includes('巩固')) return '#2196F3';
  if (stage.includes('发展')) return '#FF9800';
  if (stage.includes('初探')) return '#9E9E9E';
  return '#888';
};

// 分数颜色
const scoreColor = (score) => {
  if (score >= 4.5) return '#4CAF50';
  if (score >= 4.0) return '#2196F3';
  if (score >= 3.0) return '#FF9800';
  return '#f44336';
};

// 维度覆盖热力点颜色（对齐 Vue 的 dim-dot）
const dimDotColor = (dim) => {
  if (!dim || dim.score === 0) return 'rgba(255,255,255,0.06)';
  if (dim.isNew) return '#81C784';
  if (dim.isRefresh) return '#64B5F6';
  if (dim.score >= 4.5) return '#4CAF50';
  if (dim.score >= 3.5) return '#FF9800';
  return '#f44336';
};

// ========== 主组件 ==========
const GrowthReportPanel = ({ courseId, studentId, courseName, onClose, chartSize = 280 }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!courseId || !studentId) return;
    setLoading(true);
    setError(null);
    fetch(`${EDUGATE_API_URL}/api/evaluation/growth?courseId=${courseId}&studentId=${studentId}`)
      .then(res => res.json())
      .then(resp => {
        if (resp.code === 0 && resp.data) setData(resp.data);
        else setError(resp.message || t('growth_get_data_failed'));
      })
      .catch(err => setError(t('growth_network_error', { error: err.message })))
      .finally(() => setLoading(false));
  }, [courseId, studentId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#4CAF50' }} />
        <Typography sx={{ ml: 2, color: '#888' }}>{t('growth_loading')}</Typography>
      </Box>
    );
  }
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!data) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ color: '#888', fontSize: 14 }}>{t('growth_no_data')}</Typography></Box>;

  // 评价次数不足
  if (data.insufficient) {
    return (
      <Box sx={{ bgcolor: '#1a1a1a', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', p: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {onClose && <IconButton onClick={onClose} size="small" sx={{ color: '#888' }}><CloseIcon fontSize="small" /></IconButton>}
        </Box>
        <TimelineIcon sx={{ color: '#FF9800', fontSize: 40, mb: 1.5 }} />
        <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 600, mb: 1 }}>{t('growth_not_enabled')}</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>{data.message || t('growth_insufficient')}</Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, mt: 1.5 }}>{t('growth_completed_count', { count: data.count || 0 })}</Typography>
      </Box>
    );
  }

  // 解构数据
  const overview = data.trainingOverview || {};
  const evalSeq = data.evaluationSequence || [];
  const growthScore = data.growthScore || 0;
  const sd = data.growthScoreDetail || {};
  const dimTrends = data.dimensionTrends || {};
  const dimMaxScores = data.dimensionMaxScores || {};
  const dimAccum = data.dimensionAccumTrends || {};
  const dimStages = data.dimensionStages || [];
  const dimGrowths = data.dimensionGrowths || [];
  const contributions = data.trainingContributions || [];
  const evalLabels = evalSeq.map((e, i) => {
    const title = e.taskTitle || '';
    return title.length > 4 ? title.slice(0, 4) + '…' : (title || `#${i + 1}`);
  });

  // 最强/待提升维度
  const sortedStages = [...dimStages].sort((a, b) => (b.maxScore || 0) - (a.maxScore || 0));
  const topDims = sortedStages.slice(0, 3);
  const weakDims = sortedStages.filter(d => (d.maxScore || 0) < 4.0).sort((a, b) => (a.maxScore || 0) - (b.maxScore || 0)).slice(0, 2);

  // 精选证据 Top3（对齐 Vue 的 topEvidenceItems）
  const topEvidenceItems = (() => {
    const all = [];
    for (const dim of dimGrowths) {
      if (dim.topEvidence) {
        for (const evd of dim.topEvidence) {
          if (evd.content || evd.Content) {
            all.push({
              dimension: dim.dimensionName,
              content: evd.content || evd.Content,
              quality: (evd.specificity || evd.Specificity || 0) + (evd.relevance || evd.Relevance || 0)
            });
          }
        }
      }
    }
    return all.sort((a, b) => b.quality - a.quality).slice(0, 3);
  })();

  // 成长叙事生成（对齐 Vue 的 getTrainingNarrative）
  const getTrainingNarrative = (idx) => {
    if (!contributions[idx] || !dimTrends) return '';
    const current = contributions[idx];
    const narratives = [];

    if (idx === 0) {
      const dimCount = Object.keys(dimTrends).filter(d => (dimTrends[d]?.[0] || 0) > 0).length;
      if (dimCount > 0) narratives.push(t('growth_narrative_first_dim', { count: dimCount }));
      if (current.score >= 4.5) narratives.push(t('growth_narrative_excellent_start'));
    } else {
      const prev = contributions[idx - 1];
      if (current.score > prev.score) {
        narratives.push(t('growth_narrative_score_up', { delta: (current.score - prev.score).toFixed(1) }));
      }
      const refreshedDims = [];
      for (const [dim, scores] of Object.entries(dimTrends)) {
        const currentScore = scores[idx] || 0;
        const prevMax = Math.max(...scores.slice(0, idx), 0);
        if (currentScore > prevMax && currentScore > 0) refreshedDims.push(dim);
      }
      if (refreshedDims.length > 0) {
        if (refreshedDims.length <= 2) {
          narratives.push(t('growth_narrative_breakthrough', { dims: refreshedDims.join('」「') }));
        } else {
          narratives.push(t('growth_narrative_dim_breakthrough', { count: refreshedDims.length }));
        }
      }
      if (current.evidenceCount > (prev.evidenceCount || 0) * 1.5) {
        narratives.push(t('growth_narrative_evidence_up'));
      }
    }
    return narratives.slice(0, 2).join(t('growth_narrative_sep'));
  };

  // 维度覆盖热力图数据（对齐 Vue 的 getDimensionCoverageForTraining）
  const getDimCoverage = (idx) => {
    if (!dimTrends) return {};
    const result = {};
    for (const [dim, scores] of Object.entries(dimTrends)) {
      const score = scores[idx] || 0;
      const prevMax = idx > 0 ? Math.max(...scores.slice(0, idx), 0) : 0;
      result[dim] = {
        score,
        isNew: idx === 0 ? score > 0 : (score > 0 && prevMax === 0),
        isRefresh: score > prevMax && prevMax > 0
      };
    }
    return result;
  };

  // 精简亮点标签（对齐 Vue 的 getKeyHighlights）
  const getKeyHighlights = (inc, idx) => {
    const highlights = [];
    if (idx === 0) highlights.push({ text: t('growth_start'), color: '#2196F3' });
    if (inc.score >= 4.5) highlights.push({ text: t('growth_narrative_excellent'), color: '#4CAF50' });
    if (inc.depthScore >= 0.9) highlights.push({ text: t('growth_narrative_deep'), color: '#4CAF50' });
    if (inc.evidenceCount >= 100) highlights.push({ text: t('growth_narrative_rich'), color: '#FF9800' });
    return highlights.slice(0, 3);
  };

  // 判断最佳训练
  const bestIdx = (() => {
    if (contributions.length <= 1) return -1;
    const maxScore = Math.max(...contributions.map(c => c.score || 0));
    const indices = contributions.map((c, i) => (c.score === maxScore ? i : -1)).filter(i => i >= 0);
    return indices.length === 1 ? indices[0] : -1;
  })();

  // 分数标记颜色（对齐 Vue 的 getMarkerClass）
  const markerColor = (score) => {
    if (score >= 4.5) return '#4CAF50';
    if (score >= 4.0) return '#2196F3';
    if (score >= 3.0) return '#FF9800';
    return '#f44336';
  };

  // ========== 区块样式 ==========
  const sectionSx = { bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)', p: 2, mb: 2.5 };
  const sectionTitle = (icon, text) => (
    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon} {text}
    </Typography>
  );

  return (
    <Box sx={{ bgcolor: '#1a1a1a', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
      {/* 头部 */}
      <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TimelineIcon sx={{ color: '#2196F3', fontSize: 22 }} />
          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{t('growth_title')}</Typography>
          {courseName && <Chip label={courseName} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: 11 }} />}
        </Box>
        {onClose && <IconButton onClick={onClose} size="small" sx={{ color: '#888', '&:hover': { color: '#fff' } }}><CloseIcon fontSize="small" /></IconButton>}
      </Box>

      <Box sx={{ p: 2.5 }}>

        {/* ===== 1. 成长概况 ===== */}
        <Box sx={sectionSx}>
          {sectionTitle(null, t('growth_overview'))}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {[
              { icon: <AssignmentTurnedInIcon />, label: t('growth_participated_tasks'), value: `${overview.evaluatedTasks || evalSeq.length}${t('growth_times')}`, color: '#2196F3' },
              { icon: <EmojiEventsIcon />, label: t('growth_score'), value: growthScore.toFixed(1), color: scoreColor(growthScore) },
              { icon: <AccessTimeIcon />, label: t('growth_time_span'), value: overview.timeSpan || '-', color: '#9C27B0' },
              { icon: <BarChartIcon />, label: t('growth_accumulated_evidence_label'), value: `${sd.totalEvidenceCount || 0}${t('growth_evidence_unit')}`, color: '#FF9800' },
              { icon: <CategoryIcon />, label: t('growth_dimensions_touched'), value: `${sd.dimensionCount || dimStages.length}${t('growth_unit_count')}`, color: '#00BCD4' },
            ].map((item, i) => (
              <Box key={i} sx={{
                flex: '1 1 100px', bgcolor: `${item.color}08`, borderRadius: '10px',
                border: `1px solid ${item.color}22`, p: 1.5, textAlign: 'center', minWidth: 90,
              }}>
                <Box sx={{ color: item.color, fontSize: 20, mb: 0.5 }}>{item.icon}</Box>
                <Typography sx={{ color: item.color, fontSize: 22, fontWeight: 700 }}>{item.value}</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ===== 2. 成长评分明细 ===== */}
        {sd.maxScore > 0 && (
          <Box sx={{ ...sectionSx, bgcolor: 'rgba(76,175,80,0.04)', borderColor: 'rgba(76,175,80,0.1)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600 }}>{t('growth_score_detail')}</Typography>
              {sd.scoreLevel && <Chip label={sd.scoreLevel} size="small" sx={{ height: 22, fontSize: 10, bgcolor: 'rgba(76,175,80,0.12)', color: '#4CAF50' }} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
              {[
                { label: t('growth_ability_score'), raw: sd.maxScore, weighted: sd.weightedA, color: '#4CAF50' },
                { label: t('growth_evidence_contrib'), raw: sd.growthContribScore, weighted: sd.weightedB, color: '#2196F3' },
                { label: t('growth_engagement_score'), raw: sd.engagementScore, weighted: sd.weightedC, color: '#FF9800' },
              ].map((s, i) => (
                <Box key={i} sx={{ flex: 1, minWidth: 100, textAlign: 'center' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{s.label}</Typography>
                  <Typography sx={{ color: s.color, fontSize: 20, fontWeight: 700 }}>{(s.raw || 0).toFixed(1)}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>{t('growth_weighted', { value: (s.weighted || 0).toFixed(1) })}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(17,153,142,0.15), rgba(56,239,125,0.1))', borderRadius: '8px', p: 1.2 }}>
              <Typography sx={{ color: '#4CAF50', fontSize: 14, fontWeight: 600 }}>
                {t('growth_total_formula', {
                  a: (sd.weightedA || 0).toFixed(1),
                  b: (sd.weightedB || 0).toFixed(1),
                  c: (sd.weightedC || 0).toFixed(1),
                  total: growthScore.toFixed(1)
                })}
              </Typography>
            </Box>
            {sd.scoreInterpretation && (
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, mt: 1, fontStyle: 'italic', bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '4px', p: 1 }}>
                {sd.scoreInterpretation}
              </Typography>
            )}
          </Box>
        )}

        {/* ===== 3. 维度成长趋势 ===== */}
        {Object.keys(dimMaxScores).length > 0 && (
          <Box sx={sectionSx}>
            {sectionTitle(<TrendingUpIcon sx={{ fontSize: 16 }} />, t('growth_dimension_trend'))}
            <TrendChart dimensionMaxScores={dimMaxScores} dimensionAccumTrends={dimAccum} evalLabels={evalLabels} t={t} />
          </Box>
        )}

        {/* ===== 4. 能力画像 ===== */}
        {dimStages.length > 0 && (
          <Box sx={sectionSx}>
            {sectionTitle(null, t('growth_capability_profile'))}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {/* 雷达图 */}
              <Box sx={{ flex: '1 1 280px', minWidth: 260 }}>
                <RadarChart dimStages={dimStages} size={chartSize} />
              </Box>
              {/* 能力摘要 */}
              <Box sx={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {topDims.length > 0 && (
                  <Box>
                    <Typography sx={{ color: '#4CAF50', fontSize: 12, fontWeight: 600, mb: 1, pl: 1, borderLeft: '3px solid #4CAF50' }}>{t('growth_strong_dims')}</Typography>
                    {topDims.map((d, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, p: 1, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '6px' }}>
                        <Typography sx={{ color: '#fff', fontSize: 11, minWidth: 80, fontWeight: 500 }}>{d.dimensionName || d.DimensionName}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={(d.maxScore || 0) / 5 * 100}
                            sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50', borderRadius: 3 } }} />
                        </Box>
                        <Typography sx={{ color: '#4CAF50', fontSize: 11, fontWeight: 600, minWidth: 35 }}>{(d.maxScore || 0).toFixed(1)}{t('growth_score_unit')}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{d.totalEvidences || 0}{t('growth_evidence_unit')}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {weakDims.length > 0 && (
                  <Box>
                    <Typography sx={{ color: '#FF9800', fontSize: 12, fontWeight: 600, mb: 1, pl: 1, borderLeft: '3px solid #FF9800' }}>{t('growth_weak_dims')}</Typography>
                    {weakDims.map((d, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, p: 1, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '6px' }}>
                        <Typography sx={{ color: '#fff', fontSize: 11, minWidth: 80, fontWeight: 500 }}>{d.dimensionName || d.DimensionName}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate" value={(d.maxScore || 0) / 5 * 100}
                            sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: '#FF9800', borderRadius: 3 } }} />
                        </Box>
                        <Typography sx={{ color: '#FF9800', fontSize: 11, fontWeight: 600, minWidth: 35 }}>{(d.maxScore || 0).toFixed(1)}{t('growth_score_unit')}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* 维度详情卡片（折叠，对齐 Vue 的 el-collapse） */}
            <Accordion sx={{ bgcolor: 'transparent', boxShadow: 'none', mt: 2, '&:before': { display: 'none' }, '& .MuiAccordionSummary-root': { minHeight: 36, px: 1 } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#888', fontSize: 18 }} />}>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{t('growth_view_all_dims')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {dimStages.map((d, i) => {
                    const name = d.dimensionName || d.DimensionName || '';
                    const scoreHistory = dimMaxScores[name] || d.scoreHistory || [];
                    return (
                      <Box key={i} sx={{
                        flex: '1 1 calc(50% - 8px)', minWidth: 200, bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.05)', p: 1.5,
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                            {name.length > 12 ? name.slice(0, 12) + '…' : name}
                          </Typography>
                          <Chip label={d.stage || t('growth_stage_default')} size="small"
                            sx={{ height: 18, fontSize: 9, bgcolor: stageColor(d.stage) + '18', color: stageColor(d.stage) }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography sx={{ color: '#2196F3', fontSize: 16, fontWeight: 600 }}>
                              {(d.maxScore || 0).toFixed(1)}
                              <Typography component="span" sx={{ fontSize: 10, color: '#888' }}>/5</Typography>
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>
                              {t('growth_evidence_quality', { count: d.totalEvidences || 0, quality: d.avgQuality ? t('growth_quality_suffix', { q: d.avgQuality.toFixed(1) }) : '' })}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, mb: 0.3 }}>{t('growth_score_trend')}</Typography>
                            <MiniBarChart data={scoreHistory} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* ===== 5. 成长历程 ===== */}
        {contributions.length > 0 && (
          <Box sx={sectionSx}>
            {sectionTitle(<TimelineIcon sx={{ fontSize: 16 }} />, t('growth_journey'))}
            {contributions.map((c, idx) => {
              const isBest = idx === bestIdx;
              const narrative = getTrainingNarrative(idx);
              const coverage = getDimCoverage(idx);
              const highlights = getKeyHighlights(c, idx);
              return (
                <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 0, pb: 2 }}>
                  {/* 时间线标记 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: markerColor(c.score || 0), flexShrink: 0 }} />
                    {idx < contributions.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(255,255,255,0.08)', mt: 0.5 }} />}
                  </Box>
                  {/* 内容 */}
                  <Box sx={{
                    flex: 1, bgcolor: isBest ? 'rgba(76,175,80,0.06)' : 'rgba(255,255,255,0.02)',
                    borderRadius: '8px', p: 1.5,
                    borderLeft: `3px solid ${isBest ? '#4CAF50' : (idx === 0 ? '#2196F3' : 'rgba(255,255,255,0.08)')}`,
                  }}>
                    {/* 头部 */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{t('growth_training_n', { n: c.taskIndex || idx + 1 })}</Typography>
                        {idx === 0 && <Chip label={t('growth_start')} size="small" sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(33,150,243,0.12)', color: '#2196F3' }} />}
                        {isBest && <Chip label={t('growth_best')} size="small" sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(76,175,80,0.15)', color: '#4CAF50' }} />}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Typography sx={{ color: scoreColor(c.score || 0), fontSize: 13, fontWeight: 600 }}>{(c.score || 0).toFixed(1)}{t('growth_score_unit')}</Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{t('growth_evidence_count', { count: c.evidenceCount || 0 })}</Typography>
                        {c.avgQuality > 0 && <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{t('growth_quality', { pct: (c.avgQuality * 100).toFixed(0) })}</Typography>}
                      </Box>
                    </Box>
                    {/* 任务标题 */}
                    {c.taskTitle && <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, mb: 0.5 }}>{c.taskTitle}</Typography>}
                    {/* 成长叙事 */}
                    {narrative && (
                      <Typography sx={{ color: '#2196F3', fontSize: 11, bgcolor: 'rgba(33,150,243,0.08)', borderRadius: '4px', p: '4px 8px', mb: 0.5 }}>
                        {narrative}
                      </Typography>
                    )}
                    {/* 维度覆盖热力图 */}
                    {Object.keys(coverage).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px', mb: 0.5 }}>
                        {Object.entries(coverage).map(([dimName, dim]) => (
                          <Box key={dimName} title={`${dimName}: ${dim.score}分`}
                            sx={{
                              width: 16, height: 16, borderRadius: '3px', bgcolor: dimDotColor(dim), cursor: 'default',
                              border: dim.isNew ? '2px solid #4CAF50' : (dim.isRefresh ? '2px solid #2196F3' : 'none'),
                            }} />
                        ))}
                      </Box>
                    )}
                    {/* 亮点标签 */}
                    {highlights.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {highlights.map((h, j) => (
                          <Chip key={j} label={h.text} size="small" sx={{ height: 18, fontSize: 9, bgcolor: `${h.color}15`, color: h.color }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* ===== 6. 证据档案 ===== */}
        {dimGrowths.length > 0 && dimGrowths.some(dg => dg.topEvidence?.length > 0) && (
          <Box sx={sectionSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormatQuoteIcon sx={{ fontSize: 16 }} /> {t('growth_evidence_archive')}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
                {t('growth_total_evidence_dim', { count: sd.totalEvidenceCount || 0, dim: dimGrowths.length })}
              </Typography>
            </Box>

            {/* 精选证据 Top3 */}
            {topEvidenceItems.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: '#4CAF50', fontSize: 11, fontWeight: 600, mb: 1, pl: 1, borderLeft: '3px solid #4CAF50' }}>{t('growth_featured_evidence')}</Typography>
                {topEvidenceItems.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1, bgcolor: 'rgba(76,175,80,0.04)', borderRadius: '6px', border: '1px solid rgba(76,175,80,0.08)' }}>
                    <Chip label={item.dimension} size="small" sx={{ height: 20, fontSize: 9, bgcolor: 'rgba(76,175,80,0.12)', color: '#4CAF50', flexShrink: 0 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, lineHeight: 1.5, fontStyle: 'italic' }}>"{item.content}"</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* 各维度详情（可折叠） */}
            {dimGrowths.filter(dg => dg.topEvidence?.length > 0).slice(0, 8).map((dg, i) => (
              <Accordion key={i} sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, '& .MuiAccordionSummary-root': { minHeight: 36, px: 1 } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#888', fontSize: 16 }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                    <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 500, flex: 1 }}>{dg.dimensionName}</Typography>
                    <Chip label={`${(dg.maxScore || 0).toFixed(1)}${t('growth_score_unit')}`} size="small"
                      sx={{ height: 18, fontSize: 9, bgcolor: (dg.maxScore || 0) >= 4.5 ? 'rgba(76,175,80,0.12)' : ((dg.maxScore || 0) >= 4 ? 'rgba(33,150,243,0.1)' : 'rgba(255,152,0,0.1)'), color: (dg.maxScore || 0) >= 4.5 ? '#4CAF50' : ((dg.maxScore || 0) >= 4 ? '#2196F3' : '#FF9800') }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{dg.totalEvidences || 0}{t('growth_evidence_unit')}</Typography>
                    {/* 质量条 */}
                    {(dg.avgSpecificity || 0) > 0 && (
                      <Box sx={{ width: 50, height: 5, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ width: `${((dg.avgSpecificity || 0) + (dg.avgRelevance || 0)) / 2 * 100}%`, height: '100%', background: 'linear-gradient(90deg, #4CAF50, #2196F3)', borderRadius: 3 }} />
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0 }}>
                  {/* AI 评语 */}
                  {dg.briefAnalyses && dg.briefAnalyses.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, mb: 0.5 }}>{t('growth_ai_comment')}</Typography>
                      {dg.briefAnalyses.slice(0, 2).map((brief, bIdx) => (
                        <Box key={bIdx} sx={{ display: 'flex', gap: 1, mb: 0.5, p: '4px 8px', bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '4px' }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, flexShrink: 0 }}>{t('growth_eval_n', { n: bIdx + 1 })}</Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, lineHeight: 1.5 }}>{brief}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* 质量指标 */}
                  <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, minWidth: 35 }}>{t('eval_specificity')}</Typography>
                      <LinearProgress variant="determinate" value={(dg.avgSpecificity || 0) * 100}
                        sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: '#2196F3', borderRadius: 3 } }} />
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, minWidth: 28 }}>{(dg.avgSpecificity || 0).toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, minWidth: 35 }}>{t('eval_relevance')}</Typography>
                      <LinearProgress variant="determinate" value={(dg.avgRelevance || 0) * 100}
                        sx={{ flex: 1, height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.05)', '& .MuiLinearProgress-bar': { bgcolor: '#4CAF50', borderRadius: 3 } }} />
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, minWidth: 28 }}>{(dg.avgRelevance || 0).toFixed(2)}</Typography>
                    </Box>
                  </Box>
                  {/* 典型证据 */}
                  {dg.topEvidence && (
                    <Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, mb: 0.5 }}>{t('growth_typical_evidence')}</Typography>
                      {dg.topEvidence.slice(0, 3).map((ev, j) => (
                        <Box key={j} sx={{ mb: 0.8, pl: 1, borderLeft: '2px solid rgba(255,255,255,0.06)', py: 0.3 }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, lineHeight: 1.5, fontStyle: 'italic' }}>
                            "{ev.content || ev.Content}"
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* ===== 7. 评价详情记录 ===== */}
        {evalSeq.length > 0 && (
          <Box sx={sectionSx}>
            {sectionTitle(null, t('growth_eval_detail_record'))}
            {evalSeq.map((ev, i) => (
              <Accordion key={i} sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' }, '& .MuiAccordionSummary-root': { minHeight: 36, px: 1 } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#888', fontSize: 16 }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Chip label={`#${i + 1}`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(255,255,255,0.06)', color: '#888' }} />
                    <Typography sx={{ color: '#fff', fontSize: 11, flex: 1 }}>{ev.taskTitle || t('growth_task_n', { n: i + 1 })}</Typography>
                    <Chip label={`${(ev.comprehensiveScore || 0).toFixed(1)}${t('growth_score_unit')}`} size="small"
                      sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(76,175,80,0.12)', color: '#4CAF50' }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
                      {ev.evalTime ? new Date(ev.evalTime).toLocaleDateString('zh-CN') : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0 }}>
                  {/* 详细指标 */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                    {ev.evalTime && <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{t('growth_eval_time')}: {ev.evalTime}</Typography>}
                    {ev.submitTime && <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{t('growth_submit_time')}: {ev.submitTime}</Typography>}
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{t('growth_comprehensive_score')}: {(ev.comprehensiveScore || 0).toFixed(1)}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{t('growth_overall_level')}: {ev.overallLevel || '-'}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{t('growth_evidence_count_label')}: {ev.totalEvidenceCount || 0}{t('growth_evidence_unit')}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>{t('growth_avg_depth')}: {(ev.averageDepthScore || 0).toFixed(1)}</Typography>
                  </Box>
                  {/* 维度得分标签 */}
                  {ev.dimensionScores && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                      {Object.entries(ev.dimensionScores).map(([dim, score]) => (
                        <Chip key={dim}
                          label={`${dim.length > 5 ? dim.slice(0, 5) + '…' : dim}: ${score.toFixed(1)}`}
                          size="small"
                          sx={{
                            height: 20, fontSize: 9,
                            bgcolor: score >= 3 ? 'rgba(76,175,80,0.1)' : (score >= 2 ? 'rgba(255,255,255,0.04)' : 'rgba(255,152,0,0.1)'),
                            color: score >= 3 ? '#4CAF50' : (score >= 2 ? 'rgba(255,255,255,0.5)' : '#FF9800')
                          }} />
                      ))}
                    </Box>
                  )}
                  {/* 综合评语 */}
                  {ev.overallComment && (
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.02)', borderRadius: '4px', p: 1 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, mb: 0.3, fontWeight: 600 }}>{t('growth_narrative_comprehensive')}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, lineHeight: 1.6 }}>
                        {ev.overallComment}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GrowthReportPanel;
